import { curvanceConfigFor, curvanceLendersByChain } from '@1delta/data-sdk'
import { CurvanceOracleManagerAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { curvanceLenderKey } from '../../../lending/public-data/curvance/convertPublic'
import { getCurvanceAssetRoster } from './curvanceRoster'

/**
 * Curvance oracle price fetcher.
 *
 * One shared `OracleManager` per chain prices every asset in the protocol:
 * `getPrice(asset, inUSD=true, getLower)` returns `(price, errorCode)` in WAD
 * USD. Direct USD (PASS 1) → feeds the shared `usdPrices` map, so Monad-native
 * collateral that no other integrated protocol prices (shMON, aprMON, gMON,
 * sMON, savUSD, vUSD, syzUSD, muBOND, YZM, loAZND, …) is covered from the same
 * oracle Curvance liquidates against.
 *
 * Two properties of this feed that shape how it is used:
 *
 *  - **It is a DUAL feed with a pessimistic side.** `getLower = true` is what
 *    the protocol values COLLATERAL with; `false` is what it values DEBT with.
 *    We publish the LOWER price as the shared USD price, because a shared price
 *    map is consumed mostly for collateral valuation and understating is the
 *    safe direction.
 *
 *    `OraclePriceEntry` has no second-price field, so the upper side CANNOT be
 *    published as a value — it is read anyway because the SPREAD between the
 *    two feeds is Curvance's own divergence signal (the thing that turns into
 *    `errorCode 1 CAUTION`), and it is reported in basis points on the entry's
 *    `description`. That is the only place it survives; a debt-side consumer
 *    wanting the upper price needs a new field here, not a second fetcher.
 *  - **`errorCode` is a liveness signal, not a warning.** `1 CAUTION` means one
 *    of the two feeds is unhappy or they disagree moderately; `2 BAD_SOURCE`
 *    means the price is not usable. We DROP anything non-zero rather than
 *    publish it — a `2` price would otherwise pollute the shared map for every
 *    other protocol on the chain, and Curvance itself refuses to act on it.
 */

interface CurvanceOracleMeta {
  lender: string
  chainId: string
  marketManager: string
  /** Assets in this market, lowercase, in call order. */
  assets: string[]
}

/**
 * Two calls per asset — lower and upper. Kept adjacent so the parser's stride
 * is trivially checkable against this constant.
 */
const READS_PER_ASSET = 2

/** Build `getPrice` calls per market across every Curvance deployment. */
export function getCurvanceCalls(
  chainId: string,
): FetcherResult<CurvanceOracleMeta>[] {
  const results: FetcherResult<CurvanceOracleMeta>[] = []

  for (const lender of curvanceLendersByChain(chainId)) {
    const cfg = curvanceConfigFor(lender, chainId)
    const oracleManager = cfg?.oracleManager
    if (!oracleManager) continue

    // The market → assets map is resolved and cached by the public-data path;
    // the price fetcher deliberately does NOT re-discover it. A price fetcher
    // that ran its own discovery would double every refresh's RPC cost and
    // could disagree with the rows it is pricing.
    const roster = getCurvanceAssetRoster(chainId, lender)
    for (const market of roster) {
      if (market.assets.length === 0) continue
      const calls: MulticallCall[] = market.assets.flatMap((asset) => [
        {
          address: oracleManager,
          name: 'getPrice',
          params: [asset, true, true],
        },
        {
          address: oracleManager,
          name: 'getPrice',
          params: [asset, true, false],
        },
      ])
      results.push({
        calls,
        meta: {
          lender,
          chainId,
          marketManager: market.marketManager,
          assets: market.assets,
        },
        lender: curvanceLenderKey(lender, chainId, market.marketManager),
      })
    }
  }

  return results
}

/** Parse `(price, errorCode)` pairs into direct-USD entries. */
export function parseCurvanceResults(
  data: any[],
  meta: CurvanceOracleMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const key = curvanceLenderKey(meta.lender, chainId, meta.marketManager)
  const entries: OraclePriceEntry[] = []

  meta.assets.forEach((asset, i) => {
    const lowerRes = data?.[i * READS_PER_ASSET]
    const upperRes = data?.[i * READS_PER_ASSET + 1]
    const lower = decode(lowerRes)
    const upper = decode(upperRes)
    // A non-zero error code means Curvance itself will not act on this price.
    // Publishing it would leak a stale or diverged feed into the shared map
    // that every other protocol on the chain reads.
    if (!lower || lower.errorCode !== 0n) return

    const priceUSD = Number(lower.price) / 1e18
    // Sanity-bound, same reasoning as the Liquity/River fetchers: a mis-scaled
    // non-18-dec feed read is dropped rather than allowed to pollute the map.
    if (!(priceUSD > 0 && priceUSD < 1e9)) return

    const upperUSD =
      upper && upper.errorCode === 0n ? Number(upper.price) / 1e18 : undefined

    // Spread in bps against the published (lower) price. Widening spread is
    // the leading indicator of a feed heading for `errorCode 1`, so it is worth
    // more as a number than as an adjective.
    const spreadBps =
      upperUSD && upperUSD > priceUSD
        ? Math.round(((upperUSD - priceUSD) / priceUSD) * 10_000)
        : 0

    entries.push({
      asset,
      price: priceUSD,
      priceUSD,
      marketUid: createMarketUid(chainId, key, asset),
      targetLender: key,
      description: spreadBps
        ? `Curvance OracleManager (USD, pessimistic-low; dual feed +${spreadBps}bps)`
        : 'Curvance OracleManager (USD)',
    })
  })

  return entries
}

function decode(v: any): { price: bigint; errorCode: bigint } | undefined {
  if (!Array.isArray(v) || v.length < 2) return undefined
  try {
    return { price: BigInt(v[0]), errorCode: BigInt(v[1]) }
  } catch {
    return undefined
  }
}

/** ABI for the Curvance oracle call (`getPrice`). */
export function getCurvanceAbi() {
  return CurvanceOracleManagerAbi
}

/** Curvance Oracle Fetcher module. */
export const curvanceFetcher = {
  getCalls: getCurvanceCalls,
  parse: parseCurvanceResults,
  getAbi: getCurvanceAbi,
}
